import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useLazyGetPaginatedCustomerSolarPlantListAdminQuery } from "../../../../redux/api/dashboard/dashboardAPI";
import deyeLogo from "../../../../shared/oversight-core/assets/images/deye-logo.png";
import goodweLogo from "../../../../shared/oversight-core/assets/images/goodwe-logo.svg";
import growattLogo from "../../../../shared/oversight-core/assets/images/growatt-logo.png";
import victronLogo from "../../../../shared/oversight-core/assets/images/victron-energy-logo.png";
import IListAllSolarPlantsResponseDTO from "../../../../shared/oversight-core/dtos/response-dtos/list-all-solar-plants-response-dto";
import { ISearchFilters } from "../../../../shared/oversight-core/interfaces/search-filters";
import { IShallowSolarPlantView } from "../../../../shared/oversight-core/interfaces/shallow-solar-plant-view";
import InverterPlantCard from "../../../../shared/oversight-core/shared-components/inverter-plant-card/inverter-plant-card";
import AppSelect, {
  Option,
} from "../../../../shared/oversight-core/ui-elements/app-select/app-select";
import ModalContainer, {
  ModelContainerProps,
} from "../../../../shared/oversight-core/ui-elements/modal-container/modal-container";
import Pagination from "../../../../shared/oversight-core/ui-elements/pagination/pagination";
import Search from "../../../../shared/oversight-core/ui-elements/search/search";
import SpinnerModal from "../../../../shared/oversight-core/ui-elements/spinner/spinner";

interface IProps extends ModelContainerProps {
  show: boolean;
  solarBrandList: Option[];
  initialInverterBrand?: string;
  setSelectedPlant: React.Dispatch<
    React.SetStateAction<IShallowSolarPlantView | null>
  >;
}

const PAGE_SIZE = 4;

const initialFilters: ISearchFilters = {
  searchText: "",
  pageNumber: 0,
  pageSize: PAGE_SIZE,
};

const ConnectPlantModal = (props: IProps) => {
  const { show, setSelectedPlant, solarBrandList } = props;
  const [filters, setFilters] = useState<ISearchFilters>({
    ...initialFilters,
  });
  const [totalElements, setTotalElements] = useState(0);
  const [solarPlantList, setSolarPlantList] = useState<
    IShallowSolarPlantView[]
  >([]);
  const [searchText, setSearchText] = useState("");
  const [selectedSolarBrand, setSelectedSolarBrand] = useState<Option>({
    value: "",
    label: "",
    data: "",
  });
  const [selectedPlantId, setSelectedPlantId] = useState<string>("");

  const [triggerSolarPlantList, { isFetching }] =
    useLazyGetPaginatedCustomerSolarPlantListAdminQuery();

  // Opening the modal sets the brand and filters again with the same values, so this runs twice
  // with identical arguments. Both calls share one request; aborting the first used to cancel it
  // for the second too and left the list empty. A stale answer is ignored instead.
  useEffect(() => {
    if (!show) {
      return;
    }

    let isCurrent = true;

    triggerSolarPlantList({
      pageNumber: filters.pageNumber,
      pageSize: filters.pageSize,
      searchValue: filters.searchText,
      generationServiceProviderId: selectedSolarBrand.value,
    })
      .unwrap()
      .then((res: IListAllSolarPlantsResponseDTO) => {
        if (isCurrent) {
          setTotalElements(res.genericPage.totalElements);
          setSolarPlantList(res.genericPage.elements);
        }
      })
      .catch(() => {
        if (isCurrent) {
          setTotalElements(0);
          setSolarPlantList([]);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [
    triggerSolarPlantList,
    filters.pageNumber,
    filters.pageSize,
    filters.searchText,
    selectedSolarBrand.value,
    show,
  ]);

  useEffect(() => {
    if (show) {
      setSearchText("");
      setFilters({ ...initialFilters });
      setSelectedPlantId("");

      const initialBrand = props.initialInverterBrand
        ? solarBrandList.find((b) => b.value === props.initialInverterBrand)
        : null;

      setSelectedSolarBrand(
        initialBrand || {
          value: "",
          label: "",
          data: "",
        },
      );
    }
  }, [show, props.initialInverterBrand, solarBrandList]);

  const onSearch = () => {
    if (searchText) {
      setFilters((ps) => ({
        ...ps,
        searchText: searchText,
        pageNumber: 0,
      }));
    }
  };

  useEffect(() => {
    if (!searchText) {
      setFilters({ ...initialFilters });
    }
  }, [searchText]);

  const brandOptions: Option[] = (solarBrandList || []).map((brand) => {
    let icon = null;
    if (brand.data === "GOODWE") icon = goodweLogo;
    else if (brand.data === "Growatt") icon = growattLogo;
    else if (brand.data === "DEYE") icon = deyeLogo;
    else if (brand.data === "VRM") icon = victronLogo;

    return {
      ...brand,
      label: icon ? (
        <div className="d-flex align-items-center">
          <img
            src={icon}
            alt={brand.label as string}
            height={10}
            className="me-2"
          />
          {brand.label}
        </div>
      ) : (
        brand.label
      ),
    };
  });

  const onConfirm = () => {
    const selectedPlant =
      solarPlantList.find((plant) => plant.plantId === selectedPlantId) ?? null;
    setSelectedPlant(selectedPlant);
    props.onClose?.();
  };

  return (
    <ModalContainer
      show={show}
      title="Connect Plant"
      onClose={props.onClose}
      onCancel={props.onCancel}
      confirmButtonText="Connect"
      cancelButtonText="Cancel"
      cancelButtonVariant="transparent"
      size="modal-lg"
      disabled={!selectedPlantId}
      onConfirm={onConfirm}
    >
      <>
        <Row>
          <Col className="col-7">
            <Search
              placeholder="Search with Name or Site Name"
              onSearch={onSearch}
              handleSearchChange={(input) => setSearchText(input)}
              value={searchText}
            />
          </Col>
          <Col className="col-5">
            <AppSelect
              placeholder="Inverter Brand"
              options={brandOptions}
              selectedValue={selectedSolarBrand}
              onChangeOption={(option) => {
                setSelectedSolarBrand(option);
                setFilters((ps) => ({ ...ps, pageNumber: 0 }));
              }}
              errors={Error}
            />
          </Col>
        </Row>
        {isFetching ? (
          <SpinnerModal show={isFetching} withOverlay={false} />
        ) : (
          <>
            <div className="mt-4">
              {solarPlantList && solarPlantList.length > 0 ? (
                <>
                  {solarPlantList.map((sp, index) => {
                    return (
                      <Row key={index} className="mt-2">
                        <Col>
                          <InverterPlantCard
                            solarPlant={sp}
                            onChange={(selectedPlantId) =>
                              setSelectedPlantId(selectedPlantId)
                            }
                            checked={sp.plantId === selectedPlantId}
                          />
                        </Col>
                      </Row>
                    );
                  })}
                </>
              ) : (
                <div className="container-dash text-light text-center">
                  There are no inverters
                </div>
              )}
            </div>
            {solarPlantList.length > 0 && (
              <Pagination
                itemsPerPage={filters.pageSize ?? PAGE_SIZE}
                length={totalElements}
                currentPage={filters.pageNumber + 1}
                updateCurrentPage={(pn) => {
                  setFilters((ps) => ({ ...ps, pageNumber: pn - 1 }));
                }}
              />
            )}
          </>
        )}
      </>
    </ModalContainer>
  );
};

export default ConnectPlantModal;
